import Button from "@ui/button/Button";
import { EntityInput } from "./EntityInput";
import { useState } from "react";
import { toast } from "react-toastify";
import Config from "src/Config";
import { superAdminFetch as fetch } from "src/services/superAdminFetch";
import styled from "styled-components";
import { T } from "../adminTheme";
import { Callout, Disclosure, Fact, Facts, Fields, Muted, Page, Paged, Panel, Row, State, StatusBadge, Table } from "./adminUi"
import { UserOverviewData } from "./UserOverviewData";

interface AcceptedBin {
  bin: string;
  name: string;
}

interface RejectedBin extends AcceptedBin {
  card_type_id?: number;
  reasons: string[];
}

interface ProviderBinsResult {
  provider_id: number;
  provider_code: string;
  provider_name: string;
  source: string;
  enabled_for_company?: boolean;
  total: number;
  accepted: number;
  bins: AcceptedBin[];
  rejected: RejectedBin[];
  holder_models?: Record<string, number>;
  error: string | null;
  raw: unknown[];
}

export const SuperAdminProviderBins = ({ token }: { token: string }) => {
  const [filters, setFilters] = useState({
    provider_code: "",
    company_uid: "",
    currency: "",
    network: "",
    type: "",
    includeKyc: "",
  });
  const [results, setResults] = useState<ProviderBinsResult[] | null>(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const url = new URL(Config.scheme.URL_API + "system/providers/bins/raw");
      Object.entries(filters).forEach(([key, value]) => {
        if (value.trim()) url.searchParams.set(key, value.trim());
      });
      const response = await fetch(url.toString(), {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      const payload = await response.json();
      if (!response.ok || payload.error)
        throw new Error(payload.error?.message || response.statusText);
      setResults(payload.data ?? payload);
    } catch (error) {
      console.error(error);
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to inspect provider BINs",
      );
    } finally {
      setLoading(false);
    }
  };

  const field = (key: keyof typeof filters) => ({
    value: filters[key],
    onChange: (event: { target: { value: string } }) => setFilters((previous) => ({ ...previous, [key]: event.target.value })),
  });

  return (
    <Page>
      <Panel title="Provider BIN diagnostics" hint="Shows every card type a provider offers and why entries are filtered out of the buyer-facing BIN list.">
        <Fields>
          <label>Issuer<select {...field("provider_code")}>
            {[["wasabi", "Wasabi (sandbox)"], ["wasabiprod", "Wasabi (production)"], ["cardspro", "Cardspro"], ["paytend", "Paytend"]].map(([code, label]) => <option key={code} value={code}>{label}</option>)}
          </select></label>
          <label>Company (optional)<EntityInput token={token} kind="company" value={filters.company_uid} onChange={(value) => setFilters((previous) => ({ ...previous, company_uid: value }))} /></label>
          <label>Currency<select {...field("currency")}><option value="">Any</option><option value="USD">USD</option><option value="EUR">EUR</option></select></label>
          <label>Network<select {...field("network")}>
            <option value="">Any</option>
            <option value="1">Visa</option>
            <option value="3">Mastercard</option>
          </select></label>
          <label>Type<select {...field("type")}>
            <option value="">Any</option>
            <option value="1">Virtual</option>
          </select></label>
          <label>Cardspro KYC<select {...field("includeKyc")}>
            <option value="">Default</option>
            <option value="true">Include KYC BINs</option>
          </select></label>
        </Fields>
        <Row style={{ marginTop: 16 }}><Button variant="primarySmall" disabled={loading} onClick={load}>{loading ? "Loading…" : "Inspect provider catalogue"}</Button></Row>
      </Panel>
      <State loading={loading && !results} />
      {results && results.length === 0 && <Panel><State empty emptyText="The provider returned no card types." /></Panel>}
      {results?.map((result) => (
        <Panel
          key={result.provider_code}
          title={`${result.provider_name} (${result.provider_code})`}
          hint={result.holder_models ? `Holder models: ${Object.entries(result.holder_models).map(([key, value]) => `${key} ${value}`).join(" · ")}` : undefined}
        >
          <Facts>
            <Fact label="Source">{result.source || "—"}</Fact>
            <Fact label="Total">{result.total}</Fact>
            <Fact label="Accepted">{result.accepted}</Fact>
            <Fact label="Rejected">{result.rejected?.length ?? 0}</Fact>
            {result.enabled_for_company !== undefined && <Fact label="For this company"><StatusBadge value={result.enabled_for_company ? "Enabled" : "Disabled"} /></Fact>}
          </Facts>
          {result.error && <Callout role="alert" tone="danger" style={{ marginTop: 14 }}>{result.error}</Callout>}
          {result.bins?.length ? <div style={{ marginTop: 18 }}>
            <h3>Accepted</h3>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>{result.bins.map((item) => <Chip key={`${item.bin}-${item.name}`}>{item.name || item.bin}</Chip>)}</div>
          </div> : null}
          {result.rejected?.length ? <div style={{ marginTop: 18 }}>
            <h3>Rejected</h3>
            <Paged rows={result.rejected}>{(pageRows) => <Table style={{ marginTop: 8 }}>
              <thead><tr><th>Card type</th><th>Type ID</th><th>Why it was filtered out</th></tr></thead>
              <tbody>{pageRows.map((item, index) => <tr key={`${item.bin}-${item.card_type_id ?? index}`}>
                <td><span style={{ fontWeight: 600 }}>{item.name || item.bin}</span></td>
                <td>{item.card_type_id ?? <Muted>—</Muted>}</td>
                <td style={{ color: T.danger }}>{item.reasons.join("; ")}</td>
              </tr>)}</tbody>
            </Table>}</Paged>
          </div> : null}
          <div style={{ marginTop: 18 }}>
            <Disclosure label="Raw provider payload">
              <UserOverviewData value={result.raw} />
            </Disclosure>
          </div>
        </Panel>
      ))}
    </Page>
  );
};

const Chip = styled.span`
  display: inline-block; padding: 2px 10px; border-radius: 999px; background: ${T.hover}; color: ${T.text2}; font-size: 12.5px; font-weight: 500;
`;
